import { Startrail } from "@startbahn/startrail-sdk-js"
import { getLocale } from "@/i18n"
import { storeToRefs } from "pinia"
import { useUserStore } from "@/store/modules/user"
import { postRequest } from "./request_v2"
import { useAppStore } from "@/store/modules/app"
import { clearLoginStatus, getUserProfile, isSpecifyDevice } from "@/utils/tool"
import { ElMessage } from "element-plus"
import { getLoginError } from "@/i18n"
import router from "@/router/index"

function getLoginProviderListByDevice(withEmailPassword = false) {
  let result = []
  if (isSpecifyDevice("Android")) {
    result = ["google", "email_passwordless"]
  }
  if (isSpecifyDevice("iOS")) {
    result = ["google", "facebook", "twitter", "apple", "email_passwordless"]
  }
  result = [
    "google",
    "facebook",
    "twitter",
    "line",
    "apple",
    "email_passwordless",
  ]

  if (withEmailPassword) {
    result.push("email_password")
  }
  return result
}

/**
 *
 * @param {'google' | 'email_password' | 'facebook' | 'twitter' | 'line' | 'apple' | 'email_passwordless' } loginProvider
 * @returns
 */
export function getLoginConfig(loginProvider, withEmailPassword = false) {
  return {
    lang: getLocale(),
    authAction: { login: true, signup: false },
    wallet: "startrail",
    loginProvider: loginProvider
      ? [loginProvider]
      : getLoginProviderListByDevice(withEmailPassword),
    withModal: !loginProvider,
    customUi: {
      logoUrl: `${window.location.origin}/login-logo.png`,
      serviceName: "Startrail",
      words: {
        ja: {
          modal: {
            continueLogin: "前回の{verifier}アカウントを使う", // {verifier}は任意ですが、文言の中に存在していれば選択したログイン方法の名前へと自動的に置き換わります。
            termsConditions: "あなたの termsConditions",
            termsConditionsLinkUrl: "あなたの termsConditionsLinkUrl",
          },
          signPopup: {
            title: "ユーザログイン",
            requestFrom: "ユーザログイン",
            dataToSign: "ユーザログイン",
            confirm: "OK",
            cancel: "Cancel",
          },
          emailAuthPopup: {
            titleLogin: "ユーザログイン",
            titleSignup: "ユーザログイン",
          },
        },
        en: {},
      },
    },
    env: process.env.VUE_APP_ENV,
    apiPath: process.env.VUE_APP_API_PATH,
    auth0ClientId: process.env.VUE_APP_AUTH0_CLIENT_ID,
    auth0Domain: process.env.VUE_APP_AUTH0_DOMAIN,
    auth0TorusConfigKey: process.env.VUE_APP_AUTH0_TORUS_CONFIG_KEY,
  }
}

function postAuthMessage(eoa) {
  return postRequest("/auth/message", { eoa })
}

function postAuth(data) {
  return postRequest("/auth", data)
}

const loginTypeMap = [
  { type: "email_password", type_no: 1 },
  { type: "google", type_no: 2 },
  { type: "metamask", type_no: 3 },
  { type: "apple", type_no: 4 },
  { type: "line", type_no: 5 },
  { type: "facebook", type_no: 6 },
  { type: "twitter", type_no: 7 },
  { type: "email_passwordless", type_no: 8 },
]

function getLoginType(loginTypeOrLoginTypeNo) {
  const loginType = loginTypeMap.find(
    (item) =>
      item.type === loginTypeOrLoginTypeNo ||
      item.type_no === loginTypeOrLoginTypeNo,
  )
  return loginType
}

const observer = new MutationObserver(() => {
  const modal = document.querySelector("#torus-social-login-modal-container")
  if (modal) {
    initShadowStyle(modal)
    observer.disconnect()
  }
})

function initShadowStyle(host) {
  const styleEl = host?.shadowRoot?.querySelector("style")
  if (styleEl) {
    let cssText = styleEl.textContent
    cssText = cssText.replace(/:host\s*{/, ":host { font-size: 16px;")
    cssText = cssText.replace(/([\d.]+)rem/g, (_, num) => `${num}em`)
    styleEl.textContent = cssText
  }
}
/**
 *
 * @param {'google' | 'email_password' | 'facebook' | 'twitter' | 'line' | 'apple' | 'email_passwordless' } loginProvider
 * @returns
 */
export async function login(loginProvider) {
  const appStore = useAppStore()
  let logoutInstance = null
  let loginInstance = null
  try {
    appStore.toggleGlobalLoading(true)
    logoutInstance = new Startrail(getBaseConfig())
    await logoutInstance.logout()
    clearLoginStatus()
    observer.observe(document.body, { childList: true, subtree: true })
    loginInstance = new Startrail(getLoginConfig(loginProvider))
    const response = await loginInstance.login()
    if (!response) {
      return "cancel"
    }
    const [eoa] = response
    const { id, message } = await postAuthMessage(eoa)
    const { prefix, signature } = await loginInstance.signMessage(message)
    const { email, typeOfLogin } = await loginInstance.getUserInfo()
    const { token } = await postAuth({
      id,
      prefix,
      signature,
      address: eoa,
      email,
      user_type: 1,
      login_type: getLoginType(typeOfLogin)?.type_no || 1,
    })
    if (token) {
      const { registerEmail } = storeToRefs(useUserStore())
      localStorage.setItem("token", token)
      registerEmail.value = email
      await getUserProfile(true)
    }
  } catch (error) {
    ElMessage.error(getLoginError())
  } finally {
    observer.disconnect()
    appStore.toggleGlobalLoading(false)
    logoutInstance = null
    loginInstance = null
  }
}

function getMetaMaskConfig() {
  return {
    wallet: "metamask",
    env: process.env.VUE_APP_ENV,
    apiPath: process.env.VUE_APP_API_PATH,
  }
}

export async function mateMaskLogin() {
  const appStore = useAppStore()
  let logoutInstance = null
  let loginInstance = null
  try {
    appStore.toggleGlobalLoading(true)
    logoutInstance = new Startrail(getBaseConfig())
    await logoutInstance.logout()
    clearLoginStatus()
    loginInstance = new Startrail(getMetaMaskConfig())
    const [eoa] = await loginInstance.login()
    const { id, message } = await postAuthMessage(eoa)
    const { signature } = await loginInstance.signMessage(message)
    const { token } = await postAuth({
      id,
      signature,
      address: eoa,
      user_type: 1,
      login_type: 3,
    })
    if (token) {
      localStorage.setItem("token", token)
      await getUserProfile(true)
    }
  } catch (error) {
    console.error("MetaMask login error: ", error)
  } finally {
    appStore.toggleGlobalLoading(false)
    logoutInstance = null
    loginInstance = null
  }
}

export function getBaseConfig() {
  return {
    env: process.env.VUE_APP_ENV,
    apiPath: process.env.VUE_APP_API_PATH,
    auth0ClientId: process.env.VUE_APP_AUTH0_CLIENT_ID,
    auth0Domain: process.env.VUE_APP_AUTH0_DOMAIN,
    auth0TorusConfigKey: process.env.VUE_APP_AUTH0_TORUS_CONFIG_KEY,
  }
}

export async function logout() {
  const appStore = useAppStore()
  let logoutInstance = new Startrail(getBaseConfig())
  try {
    appStore.toggleGlobalLoading(true)
    return await logoutInstance.logout()
  } catch (error) {
    ElMessage.error("ログインに異常が発生しました。")
  } finally {
    appStore.toggleGlobalLoading(false)
    router.replace({
      path: "/",
    })
    setTimeout(() => {
      clearLoginStatus()
    }, 500)
    logoutInstance = null
  }
}

export function getTransferConfig() {
  const userInfoStr = localStorage.getItem("userInfo")
  if (userInfoStr) {
    const userInfo = JSON.parse(userInfoStr)
    if (userInfo.email) {
      const loginTypeInfo = getLoginType(userInfo.login_type)
      return getLoginConfig(
        loginTypeInfo ? loginTypeInfo.type : undefined,
        true,
      )
    } else {
      return getMetaMaskConfig()
    }
  }
  return {}
}
